import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Building2 } from 'lucide-react';

const tenantSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(64)
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  primaryDomain: z
    .string()
    .min(1, 'Primary domain is required')
    .regex(/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Enter a valid domain (e.g. example.com)'),
});

type TenantFormValues = z.infer<typeof tenantSchema>;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function AddTenantDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TenantFormValues>({
    resolver: zodResolver(tenantSchema),
    defaultValues: { name: '', slug: '', primaryDomain: '' },
  });

  const name = watch('name');

  const mutation = useMutation({
    mutationFn: (values: TenantFormValues) => tenantsApi.create(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setOpen(false);
      reset();
      setSlugTouched(false);
    },
    onError: (err: Error) => {
      setSubmitError(err.message || 'Something went wrong.');
    },
  });

  const onSubmit = (values: TenantFormValues) => {
    setSubmitError(null);
    mutation.mutate(values);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      reset();
      setSubmitError(null);
      setSlugTouched(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Building2 className="mr-2 h-4 w-4" />
          Add Tenant
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add tenant</DialogTitle>
          <DialogDescription>
            Create a new tenant and its primary email domain mapping.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tenant-name">Name</Label>
            <Input
              id="tenant-name"
              placeholder="RF3 Construction"
              {...register('name', {
                onChange: (e) => {
                  if (!slugTouched) {
                    setValue('slug', slugify(e.target.value), { shouldValidate: true });
                  }
                },
              })}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-slug">Slug</Label>
            <Input
              id="tenant-slug"
              placeholder="rf3-construction"
              {...register('slug', {
                onChange: () => setSlugTouched(true),
              })}
            />
            <p className="text-xs text-muted-foreground">
              {name
                ? `Auto-generated from name${slugTouched ? ' (edited)' : ''}. Used internally; cannot be changed later.`
                : 'Used internally; cannot be changed later.'}
            </p>
            {errors.slug && (
              <p className="text-sm text-destructive">{errors.slug.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-domain">Primary email domain</Label>
            <Input
              id="tenant-domain"
              placeholder="rf3concrete.com"
              {...register('primaryDomain')}
            />
            <p className="text-xs text-muted-foreground">
              Users signing in with Google from this domain will be routed to this tenant.
            </p>
            {errors.primaryDomain && (
              <p className="text-sm text-destructive">{errors.primaryDomain.message}</p>
            )}
          </div>

          {submitError && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2">
              {submitError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create tenant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
